// IndexedDB storage layer — replaces server-side filesystem storage
// Uses the `idb` library for a clean Promise-based API over IndexedDB

import { openDB, type IDBPDatabase } from "idb";
import type {
  AuditEntry,
  AnalysisHistoryEntry,
  PerformanceTarget,
  JiraIntegrationSettings,
  CorsProxyConfig,
  IndicatorConfig,
  GitHubSettings,
  BitbucketSettings,
  GitLabSettings,
  NotionSettings,
  GoogleDocsSettings,
  LinearIntegrationSettings,
  MondayIntegrationSettings,
  TrelloIntegrationSettings,
  AtlassianOAuth2Token,
  ConfluenceSettings,
  OAuth2Platform,
  PlatformOAuth2Token,
  AiSettings,
} from "./types";

const DB_NAME = "verigit";
const DB_VERSION = 8;

// Store names
const STORES = {
  AI_SETTINGS: "ai_settings",
  AUDIT: "audit",
  HISTORY: "history",
  TARGETS: "targets",
  JIRA_SETTINGS: "jira_settings",
  SETTINGS: "settings",
  INDICATORS: "indicators",
  INDICATOR_CODE: "indicator_code",
  REPOS: "repos",
  GITHUB_SETTINGS: "github_settings",
  LINEAR_SETTINGS: "linear_settings",
  MONDAY_SETTINGS: "monday_settings",
  TRELLO_SETTINGS: "trello_settings",
  REPORTS: "reports",
  OAUTH2_TOKENS: "oauth2_tokens",
  CONFLUENCE_SETTINGS: "confluence_settings",
  BITBUCKET_SETTINGS: "bitbucket_settings",
  GITLAB_SETTINGS: "gitlab_settings",
  NOTION_SETTINGS: "notion_settings",
  GOOGLE_DOCS_SETTINGS: "google_docs_settings",
  USERS: "users",
  SESSIONS: "sessions",
} as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

// Cross-tab coordination: when this tab's connection blocks an upgrade in another tab,
// the other tab broadcasts a 'close' message and we close ours immediately.
const DB_UPGRADE_CHANNEL = 'verigit-db-upgrade';
try {
  const _bc = new BroadcastChannel(DB_UPGRADE_CHANNEL);
  _bc.addEventListener('message', async (e) => {
    if (e.data?.type === 'close' && dbPromise) {
      try { const db = await dbPromise; db.close(); } catch {}
      dbPromise = null;
    }
  });
} catch { /* BroadcastChannel not supported — degrade gracefully */ }

export function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      blocked(_currentVersion, _blockedVersion, _event) {
        // Another tab/context has the DB open at an older version and its blocking handler
        // may not have fired (e.g. old code with broken handler, or bfcache page).
        // Broadcast a close request so it cleans up, then the upgrade can proceed.
        console.warn('[storage] DB upgrade blocked — broadcasting close request to other tabs.');
        try {
          const bc = new BroadcastChannel(DB_UPGRADE_CHANNEL);
          bc.postMessage({ type: 'close' });
          bc.close();
        } catch {}
        // Keep dbPromise alive — it will resolve once the blocking connection closes.
      },
      blocking(_currentVersion, _blockedVersion, event) {
        // This connection is holding up another tab's upgrade — close immediately.
        console.warn('[storage] This connection is blocking a DB upgrade — closing.');
        try { (event.target as IDBDatabase)?.close?.(); } catch {}
        dbPromise = null;
      },
      terminated() {
        console.warn('[storage] DB connection terminated unexpectedly — will re-open on next access.');
        dbPromise = null;
      },
      upgrade(db, oldVersion) {
        // v8: AI settings store
        if (oldVersion < 8) {
          if (!db.objectStoreNames.contains(STORES.AI_SETTINGS)) {
            db.createObjectStore(STORES.AI_SETTINGS, { keyPath: "id" });
          }
        }

        // v7: GitLab, Notion, Google Docs settings stores
        if (oldVersion < 7) {
          if (!db.objectStoreNames.contains(STORES.GITLAB_SETTINGS)) {
            db.createObjectStore(STORES.GITLAB_SETTINGS, { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains(STORES.NOTION_SETTINGS)) {
            db.createObjectStore(STORES.NOTION_SETTINGS, { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains(STORES.GOOGLE_DOCS_SETTINGS)) {
            db.createObjectStore(STORES.GOOGLE_DOCS_SETTINGS, { keyPath: "id" });
          }
        }

        // v6: Bitbucket settings store
        if (oldVersion < 6) {
          if (!db.objectStoreNames.contains(STORES.BITBUCKET_SETTINGS)) {
            db.createObjectStore(STORES.BITBUCKET_SETTINGS, { keyPath: "id" });
          }
        }

        // v5: local auth stores
        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains(STORES.USERS)) {
            const users = db.createObjectStore(STORES.USERS, { keyPath: 'id' });
            users.createIndex('by_email', 'email', { unique: true });
          }
          if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
            const sessions = db.createObjectStore(STORES.SESSIONS, { keyPath: 'token' });
            sessions.createIndex('by_userId', 'userId');
          }
        }

        // Audit log store (auto-incrementing key)
        if (!db.objectStoreNames.contains(STORES.AUDIT)) {
          const auditStore = db.createObjectStore(STORES.AUDIT, {
            keyPath: "id",
            autoIncrement: true,
          });
          auditStore.createIndex("timestamp", "timestamp");
          auditStore.createIndex("action", "action");
        }

        // Analysis history store
        if (!db.objectStoreNames.contains(STORES.HISTORY)) {
          const historyStore = db.createObjectStore(STORES.HISTORY, {
            keyPath: "id",
          });
          historyStore.createIndex("timestamp", "timestamp");
          historyStore.createIndex("repoName", "repoName");
        }

        // Performance targets store
        if (!db.objectStoreNames.contains(STORES.TARGETS)) {
          const targetsStore = db.createObjectStore(STORES.TARGETS, {
            keyPath: "id",
          });
          targetsStore.createIndex("authorName", "authorName");
          targetsStore.createIndex("status", "status");
        }

        // Jira settings store (single record)
        if (!db.objectStoreNames.contains(STORES.JIRA_SETTINGS)) {
          db.createObjectStore(STORES.JIRA_SETTINGS, { keyPath: "id" });
        }

        // General settings store (CORS proxy, etc.)
        if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
          db.createObjectStore(STORES.SETTINGS, { keyPath: "key" });
        }

        // User-created indicators store
        if (!db.objectStoreNames.contains(STORES.INDICATORS)) {
          db.createObjectStore(STORES.INDICATORS, { keyPath: "id" });
        }

        // Custom indicator code overrides
        if (!db.objectStoreNames.contains(STORES.INDICATOR_CODE)) {
          db.createObjectStore(STORES.INDICATOR_CODE, { keyPath: "id" });
        }

        // User-configured repos store
        if (!db.objectStoreNames.contains(STORES.REPOS)) {
          db.createObjectStore(STORES.REPOS, { keyPath: "id" });
        }

        // GitHub settings store (single record)
        if (!db.objectStoreNames.contains(STORES.GITHUB_SETTINGS)) {
          db.createObjectStore(STORES.GITHUB_SETTINGS, { keyPath: "id" });
        }

        // Linear settings store (single record)
        if (!db.objectStoreNames.contains(STORES.LINEAR_SETTINGS)) {
          db.createObjectStore(STORES.LINEAR_SETTINGS, { keyPath: "id" });
        }

        // Monday settings store (single record)
        if (!db.objectStoreNames.contains(STORES.MONDAY_SETTINGS)) {
          db.createObjectStore(STORES.MONDAY_SETTINGS, { keyPath: "id" });
        }

        // Trello settings store (single record)
        if (!db.objectStoreNames.contains(STORES.TRELLO_SETTINGS)) {
          db.createObjectStore(STORES.TRELLO_SETTINGS, { keyPath: "id" });
        }

        // Reports store (PDF blobs + metadata)
        if (!db.objectStoreNames.contains(STORES.REPORTS)) {
          const reportsStore = db.createObjectStore(STORES.REPORTS, { keyPath: "id" });
          reportsStore.createIndex("createdAt", "createdAt");
          reportsStore.createIndex("type", "type");
        }

        // OAuth2 tokens store (one record per platform, keyed by id)
        if (!db.objectStoreNames.contains(STORES.OAUTH2_TOKENS)) {
          db.createObjectStore(STORES.OAUTH2_TOKENS, { keyPath: "id" });
        }

        // Confluence settings store (single record)
        if (!db.objectStoreNames.contains(STORES.CONFLUENCE_SETTINGS)) {
          db.createObjectStore(STORES.CONFLUENCE_SETTINGS, { keyPath: "id" });
        }
      },
    });
    // Belt-and-suspenders: close this connection if a versionchange event fires
    // (handles cases where the blocking callback was somehow missed)
    dbPromise.then(db => {
      db.addEventListener('versionchange', () => {
        try { db.close(); } catch {}
        dbPromise = null;
      });
    }).catch(() => {});
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

// ==================== Audit Log ====================

export async function logAudit(
  entry: Omit<AuditEntry, "timestamp">
): Promise<void> {
  const db = await getDB();
  const fullEntry: AuditEntry = {
    ...entry,
    timestamp: new Date().toISOString(),
  };
  await db.add(STORES.AUDIT, fullEntry);
  console.log(
    `[AUDIT] ${fullEntry.action}:`,
    fullEntry.success ? "SUCCESS" : "FAILED",
    fullEntry.details
  );
}

export async function getAuditLog(limit: number = 100): Promise<AuditEntry[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex(STORES.AUDIT, "timestamp");
  // Return last N entries, newest first
  return all.slice(-limit).reverse();
}

// ==================== Analysis History ====================

export async function getHistory(): Promise<AnalysisHistoryEntry[]> {
  const db = await getDB();
  const all = await db.getAll(STORES.HISTORY);
  // Sort newest first
  return all.sort(
    (a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

export async function addToHistory(
  entry: AnalysisHistoryEntry
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.HISTORY, entry);

  // Keep max 100 entries
  const all = await getHistory();
  if (all.length > 100) {
    const toDelete = all.slice(100);
    const tx = db.transaction(STORES.HISTORY, "readwrite");
    for (const old of toDelete) {
      await tx.store.delete(old.id);
    }
    await tx.done;
  }

  await logAudit({
    action: "ANALYSIS_COMPLETED",
    details: {
      repoName: entry.repoName,
      commits: entry.stats.totalCommits,
      authors: entry.stats.totalAuthors,
    },
    repoName: entry.repoName,
    repoType: entry.repoType,
    success: entry.success,
    error: entry.error,
    duration: entry.duration,
  });
}

export async function clearHistory(): Promise<void> {
  const db = await getDB();
  await db.clear(STORES.HISTORY);
  await logAudit({
    action: "HISTORY_CLEARED",
    details: {},
    success: true,
  });
}

// ==================== Performance Targets ====================

export async function getTargets(): Promise<PerformanceTarget[]> {
  const db = await getDB();
  return db.getAll(STORES.TARGETS);
}

export async function getTargetById(
  id: string
): Promise<PerformanceTarget | undefined> {
  const db = await getDB();
  return db.get(STORES.TARGETS, id);
}

export async function getTargetsByAuthor(
  authorName: string
): Promise<PerformanceTarget[]> {
  const db = await getDB();
  const all = await db.getAll(STORES.TARGETS);
  return all.filter(
    (t) =>
      t.authorName.toLowerCase() === authorName.toLowerCase() &&
      t.status === "active"
  );
}

export async function createTarget(
  target: Omit<PerformanceTarget, "id" | "createdAt" | "updatedAt">
): Promise<PerformanceTarget> {
  const db = await getDB();
  const newTarget: PerformanceTarget = {
    ...target,
    id: `target-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await db.put(STORES.TARGETS, newTarget);
  return newTarget;
}

export async function updateTarget(
  id: string,
  updates: Partial<PerformanceTarget>
): Promise<PerformanceTarget | null> {
  const db = await getDB();
  const existing = await db.get(STORES.TARGETS, id);
  if (!existing) return null;

  const updated: PerformanceTarget = {
    ...existing,
    ...updates,
    updatedAt: new Date().toISOString(),
  };
  await db.put(STORES.TARGETS, updated);
  return updated;
}

export async function deleteTarget(id: string): Promise<boolean> {
  const db = await getDB();
  const existing = await db.get(STORES.TARGETS, id);
  if (!existing) return false;
  await db.delete(STORES.TARGETS, id);
  return true;
}

// ==================== Jira Settings ====================

const JIRA_SETTINGS_KEY = "default";

export async function getJiraSettings(): Promise<JiraIntegrationSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.JIRA_SETTINGS, JIRA_SETTINGS_KEY);
  return record || null;
}

export async function saveJiraSettings(
  settings: JiraIntegrationSettings
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.JIRA_SETTINGS, {
    ...settings,
    id: JIRA_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearJiraSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.JIRA_SETTINGS, JIRA_SETTINGS_KEY);
}

// ==================== General Settings ====================

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const record = await db.get(STORES.SETTINGS, key);
  return record?.value as T | undefined;
}

export async function setSetting<T>(key: string, value: T): Promise<void> {
  const db = await getDB();
  await db.put(STORES.SETTINGS, { key, value });
}

// CORS Proxy helpers
export async function getCorsProxyConfig(): Promise<CorsProxyConfig> {
  const config = await getSetting<CorsProxyConfig>("corsProxy");
  return config || { enabled: false, url: "" };
}

export async function setCorsProxyConfig(
  config: CorsProxyConfig
): Promise<void> {
  await setSetting("corsProxy", config);
}

// ==================== Indicator Storage ====================

export async function getStoredIndicators(): Promise<IndicatorConfig[]> {
  const db = await getDB();
  return db.getAll(STORES.INDICATORS);
}

export async function saveIndicator(
  indicator: IndicatorConfig
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.INDICATORS, indicator);
}

export async function deleteStoredIndicator(id: number): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.INDICATORS, id);
  // Also delete any custom code for it
  await db.delete(STORES.INDICATOR_CODE, id);
}

export async function getCustomIndicatorCode(): Promise<
  Record<number, string>
> {
  const db = await getDB();
  const all = await db.getAll(STORES.INDICATOR_CODE);
  const result: Record<number, string> = {};
  for (const entry of all) {
    result[entry.id] = entry.code;
  }
  return result;
}

export async function setCustomIndicatorCodeEntry(
  id: number,
  code: string
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.INDICATOR_CODE, { id, code });
}

export async function deleteCustomIndicatorCodeEntry(
  id: number
): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.INDICATOR_CODE, id);
}

export async function resetAllIndicatorCode(): Promise<void> {
  const db = await getDB();
  await db.clear(STORES.INDICATOR_CODE);
}

// ==================== Disabled Indicators ====================

const DISABLED_INDICATORS_KEY = "disabledIndicators";

export async function getDisabledIndicatorIds(): Promise<number[]> {
  const ids = await getSetting<number[]>(DISABLED_INDICATORS_KEY);
  return ids || [];
}

export async function setDisabledIndicatorIds(
  ids: number[]
): Promise<void> {
  await setSetting(DISABLED_INDICATORS_KEY, ids);
}

// ==================== User Repos ====================

export async function getUserRepos(): Promise<
  Array<{ id: string; name: string; type: string; url?: string }>
> {
  const db = await getDB();
  return db.getAll(STORES.REPOS);
}

export async function saveUserRepo(
  repo: { id: string; name: string; type: string; url?: string }
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.REPOS, repo);
}

export async function deleteUserRepo(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.REPOS, id);
}

// ==================== Bitbucket Settings ====================

const BITBUCKET_SETTINGS_KEY = "bitbucket-settings";

export async function getBitbucketSettings(): Promise<BitbucketSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.BITBUCKET_SETTINGS, BITBUCKET_SETTINGS_KEY);
  return record || null;
}

export async function saveBitbucketSettings(settings: BitbucketSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.BITBUCKET_SETTINGS, {
    ...settings,
    id: BITBUCKET_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearBitbucketSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.BITBUCKET_SETTINGS, BITBUCKET_SETTINGS_KEY);
}

// ==================== GitHub Settings ====================

const GITHUB_SETTINGS_KEY = "github-settings";

export async function getGitHubSettings(): Promise<GitHubSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.GITHUB_SETTINGS, GITHUB_SETTINGS_KEY);
  return record || null;
}

export async function saveGitHubSettings(settings: GitHubSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.GITHUB_SETTINGS, {
    ...settings,
    id: GITHUB_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearGitHubSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.GITHUB_SETTINGS, GITHUB_SETTINGS_KEY);
}

// ==================== Linear Settings ====================

const LINEAR_SETTINGS_KEY = "linear-settings";

export async function getLinearSettings(): Promise<LinearIntegrationSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.LINEAR_SETTINGS, LINEAR_SETTINGS_KEY);
  return record || null;
}

export async function saveLinearSettings(settings: LinearIntegrationSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.LINEAR_SETTINGS, {
    ...settings,
    id: LINEAR_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearLinearSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.LINEAR_SETTINGS, LINEAR_SETTINGS_KEY);
}

// ==================== Monday Settings ====================

const MONDAY_SETTINGS_KEY = "monday-settings";

export async function getMondaySettings(): Promise<MondayIntegrationSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.MONDAY_SETTINGS, MONDAY_SETTINGS_KEY);
  return record || null;
}

export async function saveMondaySettings(settings: MondayIntegrationSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.MONDAY_SETTINGS, {
    ...settings,
    id: MONDAY_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearMondaySettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.MONDAY_SETTINGS, MONDAY_SETTINGS_KEY);
}

// ==================== Trello Settings ====================

const TRELLO_SETTINGS_KEY = "trello-settings";

export async function getTrelloSettings(): Promise<TrelloIntegrationSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.TRELLO_SETTINGS, TRELLO_SETTINGS_KEY);
  return record || null;
}

export async function saveTrelloSettings(settings: TrelloIntegrationSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.TRELLO_SETTINGS, {
    ...settings,
    id: TRELLO_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearTrelloSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.TRELLO_SETTINGS, TRELLO_SETTINGS_KEY);
}

// ==================== Reports ====================

export interface SavedReport {
  id: string;
  createdAt: string;
  type: "main" | "individual" | "cross-project" | "combined" | "jira" | "confluence";
  filename: string;
  title: string;
  description: string;
  sizeBytes: number;
  pdfBlob: Uint8Array;
  metadata?: Record<string, unknown>;
}

const MAX_REPORTS = 50;

export async function saveReport(report: SavedReport): Promise<void> {
  const db = await getDB();
  await db.put(STORES.REPORTS, report);

  // Evict oldest if over limit
  const all = await db.getAllFromIndex(STORES.REPORTS, "createdAt");
  if (all.length > MAX_REPORTS) {
    const toDelete = all.slice(0, all.length - MAX_REPORTS);
    const tx = db.transaction(STORES.REPORTS, "readwrite");
    for (const old of toDelete) {
      await tx.store.delete(old.id);
    }
    await tx.done;
  }
}

export async function getReports(): Promise<Omit<SavedReport, "pdfBlob">[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex(STORES.REPORTS, "createdAt");
  // Return newest first, without the blob to save memory
  return all.reverse().map(({ pdfBlob: _, ...rest }) => rest);
}

export async function getReportBlob(id: string): Promise<Uint8Array | null> {
  const db = await getDB();
  const report = await db.get(STORES.REPORTS, id);
  return report?.pdfBlob || null;
}

export async function deleteReport(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.REPORTS, id);
}

export async function clearReports(): Promise<void> {
  const db = await getDB();
  await db.clear(STORES.REPORTS);
}

// ==================== OAuth2 Tokens ====================

const ATLASSIAN_TOKEN_KEY = "atlassian-oauth2";

export async function getAtlassianOAuth2Token(): Promise<AtlassianOAuth2Token | null> {
  const db = await getDB();
  const record = await db.get(STORES.OAUTH2_TOKENS, ATLASSIAN_TOKEN_KEY);
  return record || null;
}

export async function saveAtlassianOAuth2Token(
  token: Omit<AtlassianOAuth2Token, "id">
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.OAUTH2_TOKENS, { ...token, id: ATLASSIAN_TOKEN_KEY });
}

export async function clearAtlassianOAuth2Token(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.OAUTH2_TOKENS, ATLASSIAN_TOKEN_KEY);
}

// ==================== Generic Platform OAuth2 Tokens ====================
// Reuses the existing oauth2_tokens store (no DB version bump needed).
// Each platform stores its token under the key "<platform>-oauth2".

export async function getPlatformOAuth2Token(
  platform: OAuth2Platform
): Promise<PlatformOAuth2Token | null> {
  const db = await getDB();
  const record = await db.get(STORES.OAUTH2_TOKENS, `${platform}-oauth2`);
  return record || null;
}

export async function savePlatformOAuth2Token(
  token: PlatformOAuth2Token
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.OAUTH2_TOKENS, token);
}

export async function clearPlatformOAuth2Token(
  platform: OAuth2Platform
): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.OAUTH2_TOKENS, `${platform}-oauth2`);
}

// ==================== Confluence Settings ====================

const CONFLUENCE_SETTINGS_KEY = "confluence-settings";

export async function getConfluenceSettings(): Promise<ConfluenceSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.CONFLUENCE_SETTINGS, CONFLUENCE_SETTINGS_KEY);
  return record || null;
}

export async function saveConfluenceSettings(
  settings: ConfluenceSettings
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.CONFLUENCE_SETTINGS, {
    ...settings,
    id: CONFLUENCE_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearConfluenceSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.CONFLUENCE_SETTINGS, CONFLUENCE_SETTINGS_KEY);
}

// ==================== GitLab Settings ====================

const GITLAB_SETTINGS_KEY = "gitlab-settings";

export async function getGitLabSettings(): Promise<GitLabSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.GITLAB_SETTINGS, GITLAB_SETTINGS_KEY);
  return record || null;
}

export async function saveGitLabSettings(settings: GitLabSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.GITLAB_SETTINGS, {
    ...settings,
    id: GITLAB_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearGitLabSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.GITLAB_SETTINGS, GITLAB_SETTINGS_KEY);
}

// ==================== Notion Settings ====================

const NOTION_SETTINGS_KEY = "notion-settings";

export async function getNotionSettings(): Promise<NotionSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.NOTION_SETTINGS, NOTION_SETTINGS_KEY);
  return record || null;
}

export async function saveNotionSettings(settings: NotionSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.NOTION_SETTINGS, {
    ...settings,
    id: NOTION_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearNotionSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.NOTION_SETTINGS, NOTION_SETTINGS_KEY);
}

// ==================== Google Docs Settings ====================

const GOOGLE_DOCS_SETTINGS_KEY = "google-docs-settings";

export async function getGoogleDocsSettings(): Promise<GoogleDocsSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.GOOGLE_DOCS_SETTINGS, GOOGLE_DOCS_SETTINGS_KEY);
  return record || null;
}

export async function saveGoogleDocsSettings(settings: GoogleDocsSettings): Promise<void> {
  const db = await getDB();
  await db.put(STORES.GOOGLE_DOCS_SETTINGS, {
    ...settings,
    id: GOOGLE_DOCS_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearGoogleDocsSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.GOOGLE_DOCS_SETTINGS, GOOGLE_DOCS_SETTINGS_KEY);
}

// ==================== AI Settings ====================

const AI_SETTINGS_KEY = "ai-settings";

export async function getAiSettings(): Promise<AiSettings | null> {
  const db = await getDB();
  const record = await db.get(STORES.AI_SETTINGS, AI_SETTINGS_KEY);
  return record || null;
}

export async function saveAiSettings(settings: Omit<AiSettings, "id" | "updatedAt">): Promise<void> {
  const db = await getDB();
  await db.put(STORES.AI_SETTINGS, {
    ...settings,
    id: AI_SETTINGS_KEY,
    updatedAt: new Date().toISOString(),
  });
}

export async function clearAiSettings(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.AI_SETTINGS, AI_SETTINGS_KEY);
}
